import React from 'react'
import { useLocation, Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useScroll } from '@/hooks/useScroll'
import { NotificationDropdown } from '@/components/features/notifications/NotificationDropdown'

export const DesktopNav: React.FC = () => {
    const { isAuthenticated, user, logout } = useAuth()
    const { isScrolled } = useScroll()
    const { pathname } = useLocation()

    return (
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${isScrolled
                ? 'bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-slate-100 dark:border-white/5 shadow-sm'
                : 'bg-white dark:bg-slate-900 border-transparent'
            }`}>
            <div className="max-w-[1440px] mx-auto px-4 md:px-12 h-16 md:h-20 flex items-center justify-between">
                {/* Logo */}
                <Link to="/" className="flex items-center gap-2 md:gap-3 active:scale-95 transition-all group">
                    <div className="size-8 md:size-10 flex items-center justify-center group-hover:rotate-6 transition-transform duration-500">
                        <img src="/logo.svg" alt="Pelatihan Merdeka Logo" className="w-full h-full object-contain" />
                    </div>
                    <div className="flex items-baseline gap-1">
                        <span className="text-lg md:text-xl font-bold text-slate-900 dark:text-white tracking-tight">Pelatihan</span>
                        <span className="text-lg md:text-xl font-medium text-primary tracking-tight">Merdeka</span>
                    </div>
                </Link>

                {/* Navigation Links — desktop only */}
                <div className="hidden md:flex items-center gap-2">
                    <NavItem to="/" label="Home" isActive={pathname === '/'} />
                    <NavItem to="/discover" label="Discover" isActive={pathname === '/discover' || pathname.startsWith('/course')} />
                    <NavItem to="/dashboard" label="My Learning" isActive={pathname === '/dashboard'} />
                    {/* HIDDEN — Refund feature disabled until admin review page is ready */}
                    {/* {isAuthenticated && <NavItem to="/my/refunds" label="Refund" isActive={pathname === '/my/refunds'} />} */}
                    <NavItem to="/profile" label="Profile" isActive={pathname === '/profile'} />
                </div>

                {/* Auth Section — desktop only (mobile uses BottomNav) */}
                <div className="hidden md:flex items-center gap-4">
                    {!isAuthenticated ? (
                        <>
                            <Link
                                to="/login"
                                className="text-sm font-bold text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white px-4 transition-colors"
                            >
                                Masuk
                            </Link>
                            <Link
                                to="/register"
                                className="bg-slate-900 dark:bg-white text-white dark:text-slate-900 text-sm font-bold px-6 py-2.5 rounded-xl hover:opacity-90 active:scale-95 transition-all shadow-sm"
                            >
                                Daftar Gratis
                            </Link>
                        </>
                    ) : (
                        <div className="flex items-center gap-2">
                            <NotificationDropdown userId={user?.id} />
                            <Link
                                to={user?.role === 'admin' ? '/admin' : '/profile'}
                                className="flex items-center justify-center size-10 bg-slate-50 dark:bg-white/5 rounded-xl hover:bg-slate-100 dark:hover:bg-white/10 transition-all border border-slate-100 dark:border-white/5 group shadow-sm"
                                title={user?.role === 'admin' ? 'Admin Panel' : 'Profile'}
                            >
                                <div className="size-8 rounded-lg bg-primary flex items-center justify-center text-white font-bold text-xs shadow-md shadow-primary/10 group-hover:scale-110 transition-transform">
                                    {user?.name?.charAt(0) || 'U'}
                                </div>
                            </Link>
                            <button
                                onClick={() => logout()}
                                className="size-10 flex items-center justify-center text-slate-400 hover:text-rose-500 transition-colors"
                                title="Logout"
                            >
                                <span className="material-symbols-outlined">logout</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* Mobile: notification bell + avatar (no nav links — BottomNav handles that) */}
                <div className="md:hidden flex items-center gap-2">
                    {isAuthenticated ? (
                        <>
                            <NotificationDropdown userId={user?.id} />
                            <Link
                                to={user?.role === 'admin' ? '/admin' : '/profile'}
                                className="flex items-center justify-center size-9 bg-slate-50 dark:bg-white/5 rounded-xl border border-slate-100 dark:border-white/5"
                            >
                                <div className="size-7 rounded-lg bg-primary flex items-center justify-center text-white font-bold text-xs">
                                    {user?.name?.charAt(0) || 'U'}
                                </div>
                            </Link>
                        </>
                    ) : (
                        <Link
                            to="/login"
                            className="text-sm font-bold bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-4 py-2 rounded-xl"
                        >
                            Masuk
                        </Link>
                    )}
                </div>
            </div>
        </nav>
    )
}

interface NavItemProps {
    to: string
    label: string
    isActive: boolean
}

const NavItem: React.FC<NavItemProps> = ({ to, label, isActive }) => {
    return (
        <Link
            to={to}
            className={`px-5 py-2 text-sm font-bold transition-all rounded-xl relative group ${isActive
                    ? 'text-primary'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
        >
            <span>{label}</span>
            {isActive && (
                <div className="absolute -bottom-1 left-5 right-5 h-0.5 bg-primary rounded-full" />
            )}
            {!isActive && (
                <div className="absolute -bottom-1 left-5 right-5 h-0.5 bg-primary rounded-full scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center opacity-50" />
            )}
        </Link>
    )
}

export default DesktopNav
